import type { TFunction } from 'react-i18next'
import type { FetchResult } from '@apollo/client'
import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, Nil, User } from '@pbt/pbt-ui-components'

import type {
  InitiateReverseTransactionMutation,
  InitiateReverseTransactionMutationVariables,
} from '~/api/graphql/generated/types'
import { InvoiceType as GQLInvoiceType } from '~/api/graphql/generated/types'
import InvoiceType from '~/constants/InvoiceType'
import { fetchInvoice, refreshBalanceHistory } from '~/store/actions/finance'
import { fetchInvoiceV3 } from '~/store/reducers/invoiceV3'
import type { ExtendPayment } from '~/types'

import { triggerErrorDoggo } from '../../../add-payment-dialog-v2/utils/errorDoggoUtils'
import { extractBackendErrorMessage } from '../../../add-payment-dialog-v2/utils/paymentDialogUtils'
import {
  trackReverseInitiationError,
  trackReverseTransactionProcessing,
  trackReverseTransactionStarted,
} from './reverseVoidTrackingUtils'

interface InitiateReverseTransactionDependencies {
  clientId?: string | null
  currentBusiness?: Business | null
  currentUser?: User | null
  initiateReverseTransactionMutation: (options: {
    variables: InitiateReverseTransactionMutationVariables
  }) => Promise<FetchResult<InitiateReverseTransactionMutation>>
  originalPaymentId: string
  payment: ExtendPayment | Nil
  reduxDispatch: Dispatch
  setIsLoading: (isLoading: boolean) => void
  setTransactionId: (transactionId: string) => void
  t: TFunction<'Common'>
}

interface ReverseTransactionHandlerDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: Dispatch
  onClose: () => void
  onOk: () => void
  setIsLoading: (isLoading: boolean) => void
  setTransactionId: (transactionId: string) => void
  t: TFunction<'Common'>
}

interface RefreshInvoiceAndBalanceDependencies {
  clientId?: string | null
  dispatch: Dispatch
  payment?: ExtendPayment | Nil
}

/**
 * Helper to get the reverse initiation failure message
 */
export const getReverseInitiationFailureMessage = (
  t: TFunction<'Common'>,
): string => t('Common:PAYMENTS.REVERSE_INITIATION_FAILURE')

/**
 * Handles the reverse transaction mutation with error handling.
 * Initiates a reverse transaction for payments made through the new payment terminal flow (paymentTxId).
 * On success, sets the transaction ID for subscription listening.
 * On failure, triggers error doggo and resets loading state.
 */
export const initiateReverseTransaction = async (
  deps: InitiateReverseTransactionDependencies,
): Promise<boolean> => {
  const {
    currentBusiness,
    currentUser,
    initiateReverseTransactionMutation,
    originalPaymentId,
    payment,
    clientId,
    reduxDispatch,
    setIsLoading,
    setTransactionId,
    t,
  } = deps

  const defaultErrorMessage = getReverseInitiationFailureMessage(t)
  const businessId = currentBusiness?.id || ''

  // Track reverse transaction started
  trackReverseTransactionStarted({
    payment,
    businessId,
    clientId,
  })

  try {
    const result = await initiateReverseTransactionMutation({
      variables: {
        input: {
          originalPaymentId,
        },
      },
    })

    if (!result?.data?.initiateReverseTransaction || result.errors) {
      // Extract backend error from GraphQL errors if available
      const backendErrorMessage =
        result?.errors?.[0]?.message || defaultErrorMessage

      // Track reverse transaction initiation error (GraphQL error)
      trackReverseInitiationError({
        payment,
        businessId,
        clientId,
        failureReason: 'GRAPHQL_ERROR',
        errorMessage: backendErrorMessage,
      })

      triggerErrorDoggo(
        reduxDispatch,
        backendErrorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      setIsLoading(false)
      return false
    }

    const transactionId = result.data.initiateReverseTransaction.id
    setTransactionId(transactionId)

    // Track reverse transaction processing
    trackReverseTransactionProcessing({
      payment,
      businessId,
      clientId,
      transactionId,
    })

    return true
  } catch (err) {
    // Use extractBackendErrorMessage to get the most specific error available
    const backendErrorMessage = extractBackendErrorMessage(
      err,
      defaultErrorMessage,
    )

    // Track reverse transaction initiation error (network/exception)
    trackReverseInitiationError({
      payment,
      businessId,
      clientId,
      failureReason: 'INITIATION_ERROR',
      errorMessage: backendErrorMessage,
    })

    triggerErrorDoggo(
      reduxDispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    setIsLoading(false)
    return false
  }
}

/**
 * Refreshes invoices and balance history after a reverse transaction.
 * Dispatches fetchInvoice for estimates and fetchInvoiceV3 for invoices.
 * Also dispatches refreshBalanceHistory if clientId is provided.
 */
export const refreshInvoicesAndBalanceHistory = (
  deps: RefreshInvoiceAndBalanceDependencies,
): void => {
  const { dispatch, payment, clientId } = deps

  if (payment?.invoices) {
    payment.invoices.forEach((invoice) => {
      if (!invoice.id) {
        return
      }
      const isEstimate =
        invoice.invoiceType === (GQLInvoiceType.Estimate as string) ||
        invoice.invoiceType === InvoiceType.ESTIMATE

      if (isEstimate) {
        dispatch(fetchInvoice(invoice.id))
      } else {
        dispatch(fetchInvoiceV3({ id: invoice.id }))
      }
    })
  }

  if (clientId) {
    dispatch(refreshBalanceHistory(clientId))
  }
}

/**
 * Creates a success handler for reverse transaction subscription results.
 * Resets loading state, clears transaction ID, and triggers success callbacks.
 */
export const createReverseSuccessHandler = (
  deps: ReverseTransactionHandlerDependencies,
): (() => void) => {
  const { setIsLoading, setTransactionId, onOk, onClose } = deps

  return (): void => {
    setIsLoading(false)
    setTransactionId('')
    onOk()
    onClose()
  }
}

/**
 * Creates an error handler for reverse transaction subscription results.
 * Displays error message, resets loading state, and clears transaction ID.
 */
export const createReverseErrorHandler = (
  deps: ReverseTransactionHandlerDependencies,
): ((customMessage?: string) => void) => {
  const {
    currentBusiness,
    currentUser,
    dispatch,
    setIsLoading,
    setTransactionId,
    t,
  } = deps

  return (customMessage?: string): void => {
    const errorMessage = customMessage || getReverseInitiationFailureMessage(t)
    triggerErrorDoggo(dispatch, errorMessage, 500, currentUser, currentBusiness)
    setIsLoading(false)
    setTransactionId('')
  }
}
